// ID Field Rename Script - Veilige backup strategie
// Hernoemt alle 'id' velden naar 'idOud' in werkvormen database
// Kan eenvoudig worden teruggedraaid door idOud → id

import { db } from '../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
// React component for admin panel usage
import React, { useState } from 'react';

// Main function: Rename id → idOud in all werkvormen
export const renameIdFieldsToOud = async () => {
  console.log('🔄 Starting ID field rename: id → idOud');
  
  const results = {
    success: [],
    errors: [],
    summary: {}
  };

  const tredes = ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten'];

  try {
    for (const trede of tredes) {
      console.log(`📖 Processing trede: ${trede}`);
      
      try {
        // Get current trede document
        const tredeRef = doc(db, 'tredes', trede);
        const tredeSnap = await getDoc(tredeRef);
        
        if (!tredeSnap.exists()) {
          console.log(`⚠️ Trede ${trede} not found`);
          results.errors.push(`Trede ${trede} not found`);
          continue;
        }

        const tredeData = tredeSnap.data();
        
        if (!tredeData.werkvormen || !Array.isArray(tredeData.werkvormen)) {
          console.log(`⚠️ No werkvormen array found in ${trede}`);
          results.errors.push(`No werkvormen in ${trede}`);
          continue;
        }

        // Process werkvormen - rename id → idOud
        const originalCount = tredeData.werkvormen.length;
        let renamedCount = 0;
        
        const updatedWerkvormen = tredeData.werkvormen.map((werkvorm, index) => {
          // Check if werkvorm has 'id' field
          if (werkvorm.hasOwnProperty('id')) {
            // Create new werkvorm object with id renamed to idOud
            const { id, ...rest } = werkvorm;
            renamedCount++;
            
            console.log(`  📝 Werkvorm ${index}: "${werkvorm.title}" - id "${id}" → idOud`);
            
            return {
              ...rest,
              idOud: id  // Rename: id becomes idOud
            };
          } else {
            console.log(`  ⏭️ Werkvorm ${index}: "${werkvorm.title}" - no id field found`);
            return werkvorm; // No change needed
          }
        });

        // Update database only if changes were made
        if (renamedCount > 0) {
          await updateDoc(tredeRef, {
            werkvormen: updatedWerkvormen,
            lastIdRename: new Date().toISOString(),
            idRenameNote: `Renamed ${renamedCount} id fields to idOud for safe testing`
          });

          console.log(`✅ ${trede}: ${renamedCount}/${originalCount} werkvormen updated`);
          results.success.push({
            trede,
            originalCount,
            renamedCount,
            unchanged: originalCount - renamedCount
          });
        } else {
          console.log(`⏭️ ${trede}: No id fields found, skipping update`);
          results.success.push({
            trede,
            originalCount,
            renamedCount: 0,
            unchanged: originalCount
          });
        }

      } catch (error) {
        console.error(`❌ Error processing ${trede}:`, error);
        results.errors.push(`${trede}: ${error.message}`);
      }
    }

    // Generate summary
    const totalSuccess = results.success.length;
    const totalErrors = results.errors.length;
    const totalRenamed = results.success.reduce((sum, item) => sum + item.renamedCount, 0);
    
    results.summary = {
      tredesProcessed: totalSuccess,
      tredesWithErrors: totalErrors,
      totalIdFieldsRenamed: totalRenamed,
      status: totalErrors === 0 ? 'COMPLETE_SUCCESS' : 'PARTIAL_SUCCESS'
    };

    console.log('\n🎉 ID Rename Operation Complete!');
    console.log('📊 Summary:', results.summary);
    
    if (results.errors.length > 0) {
      console.log('⚠️ Errors encountered:', results.errors);
    }

    return results;

  } catch (error) {
    console.error('💥 Fatal error during ID rename operation:', error);
    throw error;
  }
};

// Reverse function: Rename idOud → id (for rollback if needed)
export const rollbackIdFields = async () => {
  console.log('🔄 Starting ID field rollback: idOud → id');
  
  const results = {
    success: [],
    errors: [],
    summary: {}
  };

  const tredes = ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten'];

  try {
    for (const trede of tredes) {
      console.log(`📖 Processing rollback for trede: ${trede}`);
      
      try {
        const tredeRef = doc(db, 'tredes', trede);
        const tredeSnap = await getDoc(tredeRef);
        
        if (!tredeSnap.exists()) {
          results.errors.push(`Trede ${trede} not found`);
          continue;
        }

        const tredeData = tredeSnap.data();
        
        if (!tredeData.werkvormen || !Array.isArray(tredeData.werkvormen)) {
          results.errors.push(`No werkvormen in ${trede}`);
          continue;
        }

        // Process rollback - rename idOud → id
        let rolledBackCount = 0;
        
        const restoredWerkvormen = tredeData.werkvormen.map((werkvorm, index) => {
          if (werkvorm.hasOwnProperty('idOud')) {
            const { idOud, ...rest } = werkvorm;
            rolledBackCount++;
            
            console.log(`  🔄 Werkvorm ${index}: "${werkvorm.title}" - idOud "${idOud}" → id`);
            
            return {
              ...rest,
              id: idOud  // Restore: idOud becomes id again
            };
          } else {
            return werkvorm;
          }
        });

        // Update database
        if (rolledBackCount > 0) {
          await updateDoc(tredeRef, {
            werkvormen: restoredWerkvormen,
            lastIdRollback: new Date().toISOString(),
            // Remove the rename tracking fields
            lastIdRename: null,
            idRenameNote: null
          });

          console.log(`✅ ${trede}: ${rolledBackCount} idOud fields restored to id`);
          results.success.push({ trede, rolledBackCount });
        } else {
          console.log(`⏭️ ${trede}: No idOud fields found`);
          results.success.push({ trede, rolledBackCount: 0 });
        }

      } catch (error) {
        console.error(`❌ Error rolling back ${trede}:`, error);
        results.errors.push(`${trede}: ${error.message}`);
      }
    }

    const totalRolledBack = results.success.reduce((sum, item) => sum + item.rolledBackCount, 0);
    
    results.summary = {
      tredesProcessed: results.success.length,
      totalIdFieldsRestored: totalRolledBack,
      errors: results.errors.length
    };

    console.log('\n🔄 Rollback Complete!');
    console.log('📊 Summary:', results.summary);

    return results;

  } catch (error) {
    console.error('💥 Fatal error during rollback:', error);
    throw error;
  }
};



export const IdFieldManager = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [operation, setOperation] = useState(null);

  const handleRename = async () => {
    if (!window.confirm('Weet je zeker dat je alle id velden wilt hernoemen naar idOud?')) {
      return;
    }

    setIsProcessing(true);
    setOperation('rename');
    
    try {
      const result = await renameIdFieldsToOud();
      setResults(result);
    } catch (error) {
      alert('Fout bij hernoemen: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRollback = async () => {
    if (!window.confirm('Weet je zeker dat je alle idOud velden terug wilt zetten naar id?')) {
      return;
    }

    setIsProcessing(true);
    setOperation('rollback');
    
    try {
      const result = await rollbackIdFields();
      setResults(result);
    } catch (error) {
      alert('Fout bij rollback: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      margin: '20px 0'
    }}>
      <h3>ID Field Management</h3>
      <p>Veilig hernoemen van id velden voor testing en cleanup.</p>
      
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button
          onClick={handleRename}
          disabled={isProcessing}
          style={{
            background: isProcessing ? '#9ca3af' : '#f59e0b',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: isProcessing ? 'not-allowed' : 'pointer'
          }}
        >
          {isProcessing && operation === 'rename' ? 'Hernoemen...' : '🔄 id → idOud'}
        </button>
        
        <button
          onClick={handleRollback}
          disabled={isProcessing}
          style={{
            background: isProcessing ? '#9ca3af' : '#10b981',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: isProcessing ? 'not-allowed' : 'pointer'
          }}
        >
          {isProcessing && operation === 'rollback' ? 'Terugzetten...' : '⏪ idOud → id'}
        </button>
      </div>

      {results && (
        <div style={{
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '15px'
        }}>
          <h4>Resultaten:</h4>
          <div style={{ fontSize: '14px' }}>
            <div>Status: {results.summary.status || 'Voltooid'}</div>
            <div>Tredes verwerkt: {results.summary.tredesProcessed || results.success.length}</div>
            <div>Velden gewijzigd: {results.summary.totalIdFieldsRenamed || results.summary.totalIdFieldsRestored || 0}</div>
            {results.errors.length > 0 && (
              <div style={{ color: '#dc2626' }}>
                Errors: {results.errors.join(', ')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Direct execution function for console use
export const executeIdRename = async () => {
  console.log('🚀 Executing ID rename operation...');
  console.log('⚠️ This will rename all "id" fields to "idOud" in werkvormen');
  
  try {
    const results = await renameIdFieldsToOud();
    
    if (results.summary.status === 'COMPLETE_SUCCESS') {
      console.log('✅ Operation completed successfully!');
      console.log(`📊 Total ID fields renamed: ${results.summary.totalIdFieldsRenamed}`);
      console.log('💡 To rollback, use: rollbackIdFields()');
    } else {
      console.log('⚠️ Operation completed with some errors');
      console.log('❌ Errors:', results.errors);
    }
    
    return results;
    
  } catch (error) {
    console.error('💥 Operation failed:', error);
    throw error;
  }
};